import { RunContextApp } from 'framework/RCApp';
import {
  Feature,
  FeatureGroupNode,
  Role,
  RbacUser,
  PermissionTreeNode,
  PermissionTreeForest,
  GetFeatureGroups,
  ModelRecordRestriction,
  ModelAttribute,
  ModelAttributeValuesPage,
} from 'types/apis/rbacInterfaces';
import { UI_REGISTRY_GROUP_KEY, UI_REGISTRY_GROUP_NAME } from 'framework/access/UiRegistry';

type ApiResult<T> = { success: boolean; data?: T; error?: string };

const unwrap = <T,>(resp: any): ApiResult<T> => {
  if ('error' in resp && resp.error) {
    const message = typeof resp.error === 'string' ? resp.error : resp.error?.message || 'Request failed';
    return { success: false, data: undefined, error: message };
  }
  if ('data' in resp && resp.data) {
    return { success: true, data: resp.data, error: undefined };
  }
  return { success: true, data: undefined, error: undefined };
};

export const RbacApis = {

  getFeatureGroups: async (rc: RunContextApp): Promise<ApiResult<GetFeatureGroups.retval>> => {
    const resp = await rc.bffClient.getFeatureGroups();
    return unwrap(resp);
  },

  getFeatures: async (rc: RunContextApp): Promise<ApiResult<Feature[]>> => {
    const resp = await rc.bffClient.getRbacFeatures();
    return unwrap(resp);
  },

  createFeature: async (
    rc: RunContextApp,
    body: { endpoint: string; path: string; methods?: string; domain?: string; label?: string },
  ): Promise<ApiResult<Feature>> => {
    const resp = await rc.bffClient.createRbacFeature(body);
    return unwrap(resp);
  },

  /**
   * Find-or-create the flat 'ui_registry' feature group that every
   * lazy-materialized ui.* feature is filed under. Never called by a sync
   * job — only from the first ever grant of a UI_PAGES entry (see
   * UiAccessPanel). Tolerates a concurrent-admin create race by refetching.
   */
  ensureUiRegistryGroup: async (rc: RunContextApp): Promise<ApiResult<FeatureGroupNode>> => {
    const findExisting = (groups: FeatureGroupNode[]) =>
      groups.find((g) => g.key === UI_REGISTRY_GROUP_KEY);

    const existingResp = await RbacApis.getFeatureGroups(rc);
    const existing = existingResp.success && existingResp.data ? findExisting(existingResp.data.groups) : undefined;
    if (existing) return { success: true, data: existing, error: undefined };

    const created = await RbacApis.createFeatureGroup(rc, {
      key: UI_REGISTRY_GROUP_KEY,
      name: UI_REGISTRY_GROUP_NAME,
    });
    if (created.success) return created;

    // Lost a create race to another admin — the group exists now, use it.
    const retryResp = await RbacApis.getFeatureGroups(rc);
    const retryExisting = retryResp.success && retryResp.data ? findExisting(retryResp.data.groups) : undefined;
    if (retryExisting) return { success: true, data: retryExisting, error: undefined };

    return created;
  },

  updateFeature: async (
    rc: RunContextApp,
    featureId: number | string,
    body: { label?: string; domain?: string },
  ): Promise<ApiResult<Feature>> => {
    const resp = await rc.bffClient.updateRbacFeature(featureId, body);
    return unwrap(resp);
  },

  addFeaturesToGroup: async (
    rc: RunContextApp,
    groupId: number | string,
    featureIds: Array<number | string>,
  ): Promise<ApiResult<{ moved: number[] }>> => {
    const resp = await rc.bffClient.addFeaturesToGroup(groupId, featureIds);
    return unwrap(resp);
  },

  removeFeatureFromGroup: async (
    rc: RunContextApp,
    groupId: number | string,
    featureId: number | string,
  ): Promise<ApiResult<{ removed: boolean }>> => {
    const resp = await rc.bffClient.removeFeatureFromGroup(groupId, featureId);
    return unwrap(resp);
  },

  updateFeatureGroup: async (
    rc: RunContextApp,
    groupId: number | string,
    body: { name?: string; description?: string; parent_group_id?: number | string | null; sort_order?: number },
  ): Promise<ApiResult<FeatureGroupNode>> => {
    const resp = await rc.bffClient.updateFeatureGroup(groupId, body);
    return unwrap(resp);
  },

  createFeatureGroup: async (
    rc: RunContextApp,
    body: { key: string; name: string; description?: string; parent_group_id?: number | string | null; sort_order?: number },
  ): Promise<ApiResult<FeatureGroupNode>> => {
    const resp = await rc.bffClient.createFeatureGroup(body);
    return unwrap(resp);
  },

  deleteFeatureGroup: async (
    rc: RunContextApp,
    groupId: number | string,
    strategy?: 'block' | 'reparent',
  ): Promise<ApiResult<{ deleted?: boolean; reparented_groups?: number; reparented_features?: number }>> => {
    const resp = await rc.bffClient.deleteFeatureGroup(groupId, strategy);
    return unwrap(resp);
  },

  getRoles: async (rc: RunContextApp): Promise<ApiResult<Role[]>> => {
    const resp = await rc.bffClient.getRoles();
    return unwrap(resp);
  },

  getUsers: async (rc: RunContextApp): Promise<ApiResult<RbacUser[]>> => {
    const resp = await rc.bffClient.getRbacUsers();
    return unwrap(resp);
  },

  getRolePermissionTree: async (rc: RunContextApp, roleId: number | string): Promise<ApiResult<PermissionTreeForest>> => {
    const resp = await rc.bffClient.getRolePermissionTree(roleId);
    return unwrap(resp);
  },

  getUserPermissionTree: async (rc: RunContextApp, userId: number | string): Promise<ApiResult<PermissionTreeForest>> => {
    const resp = await rc.bffClient.getUserPermissionTree(userId);
    return unwrap(resp);
  },

  grantRoleFeatureGroup: async (rc: RunContextApp, roleId: number | string, groupId: number | string): Promise<ApiResult<Role>> => {
    const resp = await rc.bffClient.grantRoleFeatureGroup(roleId, groupId);
    return unwrap(resp);
  },

  revokeRoleFeatureGroup: async (rc: RunContextApp, roleId: number | string, groupId: number | string): Promise<ApiResult<void>> => {
    const resp = await rc.bffClient.revokeRoleFeatureGroup(roleId, groupId);
    return unwrap(resp);
  },

  attachRoleFeatures: async (rc: RunContextApp, roleId: number | string, featureIds: Array<number | string>): Promise<ApiResult<Role>> => {
    const resp = await rc.bffClient.attachRoleFeatures(roleId, featureIds);
    return unwrap(resp);
  },

  detachRoleFeatures: async (rc: RunContextApp, roleId: number | string, featureIds: Array<number | string>): Promise<ApiResult<Role>> => {
    const resp = await rc.bffClient.detachRoleFeatures(roleId, featureIds);
    return unwrap(resp);
  },

  grantUserFeatureGroup: async (rc: RunContextApp, userId: number | string, groupId: number | string): Promise<ApiResult<RbacUser>> => {
    const resp = await rc.bffClient.grantUserFeatureGroup(userId, groupId);
    return unwrap(resp);
  },

  revokeUserFeatureGroup: async (rc: RunContextApp, userId: number | string, groupId: number | string): Promise<ApiResult<void>> => {
    const resp = await rc.bffClient.revokeUserFeatureGroup(userId, groupId);
    return unwrap(resp);
  },

  grantUserFeature: async (rc: RunContextApp, userId: number | string, featureId: number | string): Promise<ApiResult<RbacUser>> => {
    const resp = await rc.bffClient.grantUserFeature(userId, featureId);
    return unwrap(resp);
  },

  revokeUserFeature: async (rc: RunContextApp, userId: number | string, featureId: number | string): Promise<ApiResult<void>> => {
    const resp = await rc.bffClient.revokeUserFeature(userId, featureId);
    return unwrap(resp);
  },

  getEnforcedModels: async (rc: RunContextApp): Promise<ApiResult<{ models: string[] }>> => {
    const resp = await rc.bffClient.getEnforcedModels();
    return unwrap(resp);
  },

  getModelAttributes: async (rc: RunContextApp, model: string): Promise<ApiResult<{ attributes: ModelAttribute[] }>> => {
    const resp = await rc.bffClient.getModelAttributes(model);
    return unwrap(resp);
  },

  getModelAttributeValues: async (
    rc: RunContextApp,
    model: string,
    attribute: string,
    params: { q?: string; page?: number; per_page?: number } = {},
  ): Promise<ApiResult<ModelAttributeValuesPage>> => {
    const resp = await rc.bffClient.getModelAttributeValues(model, attribute, params);
    return unwrap(resp);
  },

  getRoleModelRestrictions: async (rc: RunContextApp, roleId: number | string): Promise<ApiResult<ModelRecordRestriction[]>> => {
    const resp = await rc.bffClient.getRoleModelRestrictions(roleId);
    return unwrap(resp);
  },

  getUserModelRestrictions: async (rc: RunContextApp, userId: number | string): Promise<ApiResult<ModelRecordRestriction[]>> => {
    const resp = await rc.bffClient.getUserModelRestrictions(userId);
    return unwrap(resp);
  },

  createRoleModelRestriction: async (
    rc: RunContextApp,
    roleId: number | string,
    body: { model: string; attribute: string; values: string[] },
  ): Promise<ApiResult<ModelRecordRestriction>> => {
    const resp = await rc.bffClient.createRoleModelRestriction(roleId, body);
    return unwrap(resp);
  },

  updateRoleModelRestriction: async (
    rc: RunContextApp,
    roleId: number | string,
    restrictionId: number | string,
    body: { attribute?: string; values?: string[] },
  ): Promise<ApiResult<ModelRecordRestriction>> => {
    const resp = await rc.bffClient.updateRoleModelRestriction(roleId, restrictionId, body);
    return unwrap(resp);
  },

  deleteRoleModelRestriction: async (
    rc: RunContextApp,
    roleId: number | string,
    restrictionId: number | string,
  ): Promise<ApiResult<void>> => {
    const resp = await rc.bffClient.deleteRoleModelRestriction(roleId, restrictionId);
    return unwrap(resp);
  },

  createUserModelRestriction: async (
    rc: RunContextApp,
    userId: number | string,
    body: { model: string; attribute: string; values: string[] },
  ): Promise<ApiResult<ModelRecordRestriction>> => {
    const resp = await rc.bffClient.createUserModelRestriction(userId, body);
    return unwrap(resp);
  },

  updateUserModelRestriction: async (
    rc: RunContextApp,
    userId: number | string,
    restrictionId: number | string,
    body: { attribute?: string; values?: string[] },
  ): Promise<ApiResult<ModelRecordRestriction>> => {
    const resp = await rc.bffClient.updateUserModelRestriction(userId, restrictionId, body);
    return unwrap(resp);
  },

  deleteUserModelRestriction: async (
    rc: RunContextApp,
    userId: number | string,
    restrictionId: number | string,
  ): Promise<ApiResult<void>> => {
    const resp = await rc.bffClient.deleteUserModelRestriction(userId, restrictionId);
    return unwrap(resp);
  },
};

// Re-export for component convenience
export type { Feature, FeatureGroupNode, Role, RbacUser, PermissionTreeNode, ModelRecordRestriction };
